import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import { getDoorHistory } from "../../../Redux/Reports/ReportsActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import toastHelper from "../../../Util/toastHelper";
import React, { useEffect, useState } from "react";
import styles from "./DoorHistory.module.scss";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

interface DoorHistoryType {
  amenityName: string;
  buildingName: string;
  createdAt: string;
  doorName: string;
  memberName: string;
  memberPhoneNumber: string;
  memberEmail: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

const columnDefs: ColDef<DoorHistoryType>[] = [
  {
    headerName: "Building",
    field: "buildingName",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Member",
    field: "memberName",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Member Email",
    field: "memberEmail",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Member Phone Number",
    field: "memberPhoneNumber",
    flex: 1,
  },

  {
    headerName: "Amenity Name",
    field: "amenityName",
    maxWidth: 170,
    flex: 1,
  },

  {
    headerName: "Door Name",
    field: "doorName",
    maxWidth: 170,
    flex: 1,
  },

  {
    headerName: "Log Date Time",
    field: "createdAt",
    minWidth: 150,
    valueGetter: (params) => {
      return moment(params?.data?.createdAt).format("MM/DD/YYYY hh:mm A");
    },
    flex: 1,
  },
];

function DoorHistory() {
  const [rowData, setRowData] = useState<DoorHistoryType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const { doorHistory, doorHistoryLoading } = useSelector(
    (state: RootState) => state.reports,
  );
  const { buildings } = useSelector((state: RootState) => state.buildings);
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const getHistoryHandler = () => {
    if (!buildingFilter) {
      toastHelper.error("Please select a building");
    }
    if (buildingFilter && !doorHistoryLoading) {
      dispatch(
        getDoorHistory({
          buildingId: buildingFilter,
        }),
      );
    }
  };

  const onDatesChange = (value: string, key: string) => {
    if (key === "startDate") {
      if (moment(value).isAfter(moment(endDate))) {
        toastHelper.error("Start date must be before end date");
      } else {
        setStartDate(value);
      }
    }

    if (key === "endDate") {
      if (moment(value).isBefore(moment(startDate))) {
        toastHelper.error("End date must be after start date");
      } else {
        setEndDate(value);
      }
    }
  };

  const applyFilters = (rowData: DoorHistoryType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (log: DoorHistoryType) =>
          log?.memberName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          log?.doorName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          log?.amenityName?.toLowerCase()?.includes(search?.toLowerCase()),
      );
    }

    if (startDate) {
      array = array.filter((log: DoorHistoryType) => {
        const createdAt = moment(log?.createdAt);
        return createdAt.isSameOrAfter(moment(startDate), "day");
      });
    }

    if (endDate) {
      array = array.filter((log: DoorHistoryType) => {
        const createdAt = moment(log?.createdAt);
        return createdAt.isSameOrBefore(moment(endDate), "day");
      });
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (doorHistory) {
      setRowData(doorHistory);
    }
  }, [doorHistory]);

  if (doorHistoryLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Door History</h1>
        </div>
        <div className={styles.buttonContainer}>
          {filteredRowData?.length > 0 && (
            <button
              className={styles.button}
              onClick={() =>
                exportToExcel({
                  data: filteredRowData || [],
                  fileName: "Profit Report",
                })
              }
            >
              Export
            </button>
          )}
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.doubleInputContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.searchContainerInner}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value || "")}
              />
              <div
                className={styles.iconContainer}
                onClick={() => setSearch("")}
              >
                <IoCloseSharp className={styles.icon} />
              </div>
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Start Date</p>
              <input
                type="date"
                className={styles.input}
                value={startDate}
                onChange={(e) => {
                  onDatesChange(e?.target?.value || "", "startDate");
                }}
              />
            </div>
            <div className={styles.filterItem}>
              <p className={styles.label}>End Date</p>
              <input
                type="date"
                className={styles.input}
                value={endDate}
                onChange={(e) => {
                  onDatesChange(e?.target?.value || "", "endDate");
                }}
              />
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Building</p>
              <select
                className={styles.input}
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e?.target?.value || "")}
              >
                <option value="">Select a building</option>
                {buildings?.map((building: any, i: number) => {
                  return (
                    <option key={i} value={building?.buildingId}>
                      {building?.buildingName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={styles.buttonContainer}>
              <button className={styles.button} onClick={getHistoryHandler}>
                {" "}
                Get History
              </button>
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
}

export default DoorHistory;
